/**
 * AdminNummernkreiseSection — Rechnungsnummernkreise verwalten (Issue #484 + #485)
 *
 * Admin-UI für /einstellungen/nummernkreise:
 * - Tab "Konfiguration": Tabelle, Anlegen, Bearbeiten, GoBD-Hinweise
 * - Tab "Bestandsanalyse": View rechnungsnummern_abweichungen, Filter, CSV-Export
 */

import { useState, useCallback } from 'react';
import { Plus, Edit2, AlertTriangle, RefreshCw, Hash, Eye, EyeOff, CircleSlash, BarChart2, Settings2 } from 'lucide-react';
import AdminNummernkreiseBestandSection from '@/components/AdminNummernkreiseBestandSection';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useBetriebe, useNummernkreise, type NummernkreisMitDetails } from '@/hooks/useSupabase';
import { useMarken } from '@/hooks/useMarken';
import type { NummernkreisInsert, NummernkreisUpdate } from '@/lib/database.types';

const CURRENT_YEAR = new Date().getFullYear();
const PRAEFIX_REGEX = /^[A-Za-z][A-Za-z0-9-]*$/;

function formatVorschau(praefix: string, jahr: number, naechste: number, stellen: number): string {
  if (!praefix) return '—';
  return `${praefix}-${jahr}-${String(naechste).padStart(stellen, '0')}`;
}

// ─── Neuer-Kreis-Dialog ────────────────────────────────────────────────────

interface NeuerKreisDialogProps {
  betriebe: { id: string; name: string; typ: string }[];
  marken: { id: string; marken_name: string; betrieb_id: string | null }[];
  onSave: (data: NummernkreisInsert) => Promise<boolean>;
  onClose: () => void;
}

function NeuerKreisDialog({ betriebe, marken, onSave, onClose }: NeuerKreisDialogProps) {
  const [betriebId, setBetriebId] = useState(betriebe[0]?.id ?? '');
  const [markeId, setMarkeId] = useState('');
  const [praefix, setPraefix] = useState('');
  const [jahr, setJahr] = useState(CURRENT_YEAR);
  const [naechsteNummer, setNaechsteNummer] = useState(1);
  const [stellen, setStellen] = useState(4);
  const [saving, setSaving] = useState(false);

  const filteredMarken = marken.filter(m => !betriebId || m.betrieb_id === betriebId);
  const praefixValid = PRAEFIX_REGEX.test(praefix);
  const canSave = betriebId && praefixValid && naechsteNummer >= 1;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    const ok = await onSave({
      betrieb_id: betriebId,
      marke_id: markeId || null,
      praefix: praefix.toUpperCase(),
      jahr,
      naechste_nummer: naechsteNummer,
      stellen,
      aktiv: true,
    });
    if (ok) onClose();
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-bold text-gray-900">Neuer Nummernkreis</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center">
            <CircleSlash className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Betrieb */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Betrieb *</label>
            <select
              name="betrieb_id"
              value={betriebId}
              onChange={e => { setBetriebId(e.target.value); setMarkeId(''); }}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20"
            >
              {betriebe.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          {/* Marke (optional) */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Marke <span className="text-gray-400">(optional — leer = betriebsweiter Kreis)</span>
            </label>
            <select
              value={markeId}
              onChange={e => setMarkeId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20"
            >
              <option value="">Kein Marken-Bezug (betriebsweit)</option>
              {filteredMarken.map(m => (
                <option key={m.id} value={m.id}>{m.marken_name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Präfix */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Präfix *</label>
              <input
                name="praefix"
                type="text"
                value={praefix}
                onChange={e => setPraefix(e.target.value.toUpperCase())}
                placeholder="z. B. GA, WS, MG"
                maxLength={10}
                className={`w-full px-3 py-2 rounded-lg border text-sm font-mono focus:outline-none focus:ring-2 ${
                  praefix && !praefixValid
                    ? 'border-red-300 focus:ring-red-200'
                    : 'border-gray-200 focus:ring-[#9B1C1C]/20'
                }`}
              />
              {praefix && !praefixValid && (
                <p className="text-[10px] text-red-500 mt-0.5">Muss mit Buchstaben beginnen; Buchstaben, Ziffern und Bindestrich erlaubt</p>
              )}
            </div>

            {/* Jahr */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Jahr *</label>
              <input
                name="jahr"
                type="number"
                value={jahr}
                min={2020}
                max={2100}
                onChange={e => setJahr(Number(e.target.value))}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Startnummer */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Startnummer</label>
              <input
                type="number"
                value={naechsteNummer}
                min={1}
                onChange={e => setNaechsteNummer(Math.max(1, Number(e.target.value)))}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20"
              />
            </div>

            {/* Stellen */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Stellen</label>
              <input
                type="number"
                value={stellen}
                min={1}
                max={10}
                onChange={e => setStellen(Math.max(1, Math.min(10, Number(e.target.value))))}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20"
              />
            </div>
          </div>

          {/* Vorschau */}
          {praefixValid && praefix && (
            <div className="bg-gray-50 rounded-lg px-4 py-3 flex items-center gap-2">
              <Hash className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">Nächste Nummer</p>
                <p className="text-sm font-mono font-semibold text-gray-800" data-testid="nummer-vorschau">
                  {formatVorschau(praefix, jahr, naechsteNummer, stellen)}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave || saving}
            className="px-4 py-2 rounded-lg bg-[#9B1C1C] text-white text-sm font-medium hover:bg-[#7A1616] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Anlegen…' : 'Anlegen'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Bearbeiten-Dialog ────────────────────────────────────────────────────

interface BearbeitenDialogProps {
  kreis: NummernkreisMitDetails;
  onSave: (id: string, update: NummernkreisUpdate) => Promise<boolean>;
  onClose: () => void;
}

function BearbeitenDialog({ kreis, onSave, onClose }: BearbeitenDialogProps) {
  const [praefix, setPraefix] = useState(kreis.praefix);
  const [stellen, setStellen] = useState(kreis.stellen);
  const [naechsteNummer, setNaechsteNummer] = useState(kreis.naechste_nummer);
  const [saving, setSaving] = useState(false);

  const praefixValid = PRAEFIX_REGEX.test(praefix);
  const isDecrement = naechsteNummer < kreis.naechste_nummer;
  const hasBeenUsed = kreis.naechste_nummer > 1;

  const handleSave = async () => {
    setSaving(true);
    const ok = await onSave(kreis.id, {
      praefix: praefix.toUpperCase(),
      stellen,
      naechste_nummer: naechsteNummer,
    });
    if (ok) onClose();
    setSaving(false);
  };

  const canSave = praefixValid && naechsteNummer >= 1 && !saving;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-sm font-bold text-gray-900">Nummernkreis bearbeiten</h2>
            <p className="text-[11px] text-gray-400">
              {kreis.betrieb?.name ?? '—'}{kreis.marke ? ` · ${kreis.marke.marken_name}` : ''} · {kreis.jahr}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center">
            <CircleSlash className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* GoBD-Hinweis bei Decrement */}
          {isDecrement && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex gap-3">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-amber-800">GoBD-Hinweis: Lückenlosigkeit</p>
                <p className="text-[11px] text-amber-700 mt-0.5">
                  Eine Reduktion der nächsten Nummer kann Rechnungsnummern-Lücken erzeugen und verstößt
                  gegen die GoBD-Anforderung der lückenlosen, unveränderlichen Nummerierung.
                  Nur fortführen wenn Sie sicher sind, dass keine Nummern in diesem Bereich vergeben wurden.
                </p>
              </div>
            </div>
          )}

          {/* GoBD-Info bei erstmaliger Bearbeitung ohne Decrement */}
          {hasBeenUsed && !isDecrement && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 flex gap-3">
              <AlertTriangle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-blue-700">
                Nummern wurden bereits vergeben (ab {kreis.naechste_nummer}). Änderungen des Präfixes
                gelten nur für zukünftige Nummern — bereits ausgestellte Rechnungen sind davon
                unberührt.
              </p>
            </div>
          )}

          {/* Präfix */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Präfix</label>
            <input
              type="text"
              value={praefix}
              onChange={e => setPraefix(e.target.value.toUpperCase())}
              maxLength={10}
              className={`w-full px-3 py-2 rounded-lg border text-sm font-mono focus:outline-none focus:ring-2 ${
                !praefixValid ? 'border-red-300 focus:ring-red-200' : 'border-gray-200 focus:ring-[#9B1C1C]/20'
              }`}
            />
            {!praefixValid && (
              <p className="text-[10px] text-red-500 mt-0.5">Muss mit Buchstaben beginnen; Buchstaben, Ziffern und Bindestrich erlaubt</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Nächste Nummer */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Nächste Nummer</label>
              <input
                type="number"
                value={naechsteNummer}
                min={1}
                onChange={e => setNaechsteNummer(Math.max(1, Number(e.target.value)))}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20"
              />
            </div>

            {/* Stellen */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Stellen</label>
              <input
                type="number"
                value={stellen}
                min={1}
                max={10}
                onChange={e => setStellen(Math.max(1, Math.min(10, Number(e.target.value))))}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20"
              />
            </div>
          </div>

          {/* Vorschau */}
          <div className="bg-gray-50 rounded-lg px-4 py-3 flex items-center gap-2">
            <Hash className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Nächste Nummer (Vorschau)</p>
              <p className="text-sm font-mono font-semibold text-gray-800" data-testid="nummer-vorschau">
                {formatVorschau(praefixValid ? praefix : kreis.praefix, kreis.jahr, naechsteNummer, stellen)}
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="px-4 py-2 rounded-lg bg-[#9B1C1C] text-white text-sm font-medium hover:bg-[#7A1616] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Hauptkomponente ─────────────────────────────────────────────────────────

type NummernkreiseTab = 'konfiguration' | 'bestandsanalyse';

export default function AdminNummernkreiseSection() {
  const [activeTab, setActiveTab] = useState<NummernkreiseTab>('konfiguration');
  const { data: kreise, loading, error, refetch } = useNummernkreise();
  const { data: betriebe } = useBetriebe();
  const { data: marken } = useMarken();
  const [showNeuerKreis, setShowNeuerKreis] = useState(false);
  const [editingKreis, setEditingKreis] = useState<NummernkreisMitDetails | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  };

  const handleCreate = useCallback(async (data: NummernkreisInsert): Promise<boolean> => {
    const { error } = await supabase.from('nummernkreise').insert(data);
    if (error) {
      if (error.code === '23505') {
        toast.error('Ein Nummernkreis für diese Kombination aus Betrieb, Marke und Jahr existiert bereits.');
      } else {
        toast.error(`Fehler: ${error.message}`);
      }
      return false;
    }
    toast.success('Nummernkreis angelegt');
    refetch();
    return true;
  }, [refetch]);

  const handleUpdate = useCallback(async (id: string, update: NummernkreisUpdate): Promise<boolean> => {
    const { error } = await supabase.from('nummernkreise').update(update).eq('id', id);
    if (error) {
      toast.error(`Fehler: ${error.message}`);
      return false;
    }
    toast.success('Nummernkreis aktualisiert');
    refetch();
    return true;
  }, [refetch]);

  const handleToggleAktiv = useCallback(async (kreis: NummernkreisMitDetails) => {
    const { error } = await supabase
      .from('nummernkreise')
      .update({ aktiv: !kreis.aktiv })
      .eq('id', kreis.id);
    if (error) {
      toast.error(`Fehler: ${error.message}`);
      return;
    }
    toast.success(kreis.aktiv ? 'Nummernkreis deaktiviert' : 'Nummernkreis aktiviert');
    refetch();
  }, [refetch]);

  // Marken für den Dialog — mit betrieb_id angereichert
  const markenFuerDialog = marken.map(m => ({
    id: m.id,
    marken_name: m.marken_name,
    betrieb_id: m.betrieb_id ?? null,
  }));

  return (
    <div data-testid="nummernkreise-section">
      {/* Tab-Navigation */}
      <div className="flex gap-1 mb-4">
        <button
          onClick={() => setActiveTab('konfiguration')}
          data-testid={activeTab === 'konfiguration' ? undefined : undefined}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${
            activeTab === 'konfiguration'
              ? 'bg-[#9B1C1C] text-white'
              : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Settings2 className="w-3.5 h-3.5" />
          Konfiguration
        </button>
        <button
          onClick={() => setActiveTab('bestandsanalyse')}
          data-testid="nummernkreise-bestand-tab"
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${
            activeTab === 'bestandsanalyse'
              ? 'bg-[#9B1C1C] text-white'
              : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <BarChart2 className="w-3.5 h-3.5" />
          Bestandsanalyse
        </button>
      </div>

      {activeTab === 'bestandsanalyse' ? (
        <AdminNummernkreiseBestandSection />
      ) : (
        <>
          {/* Header Konfiguration */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 shadow-sm">
            <div className="flex items-center justify-between mb-1">
              <div>
                <h3 className="text-sm font-bold text-gray-900">Rechnungsnummernkreise</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Konfigurieren Sie Präfix, Jahreskreis und Stellen für jeden Betrieb / jede Marke.
                  GoBD: Soft-Disable statt Löschen — keine Lücken in der Nummerierung.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="w-8 h-8 rounded-lg border border-gray-200 hover:bg-gray-50 flex items-center justify-center transition-colors"
                  title="Aktualisieren"
                >
                  <RefreshCw className={`w-3.5 h-3.5 text-gray-400 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => setShowNeuerKreis(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#9B1C1C] text-white text-xs font-medium hover:bg-[#7A1616] transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Neuer Kreis
                </button>
              </div>
            </div>
          </div>

          {/* Tabelle */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {loading && (
              <div className="flex items-center justify-center py-12 text-sm text-gray-400">
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                Lade Nummernkreise…
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 px-5 py-4 text-sm text-red-600">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {!loading && !error && (kreise ?? []).length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <Hash className="w-8 h-8 mb-2 opacity-40" />
                <p className="text-sm font-medium">Noch keine Nummernkreise</p>
                <p className="text-xs mt-1">Legen Sie den ersten Nummernkreis über „Neuer Kreis" an.</p>
              </div>
            )}

            {!loading && !error && (kreise ?? []).length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                      Betrieb / Marke
                    </th>
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                      Präfix
                    </th>
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                      Jahr
                    </th>
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                      Nächste Nr.
                    </th>
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                      Stellen
                    </th>
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                      Vorschau
                    </th>
                    <th className="py-2.5 px-4 text-[11px] font-semibold text-gray-400 uppercase tracking-wider w-24">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(kreise ?? []).map(k => (
                    <tr
                      key={k.id}
                      className={`border-b border-gray-50 transition-colors ${k.aktiv ? 'hover:bg-gray-50' : 'bg-gray-50 opacity-60'}`}
                    >
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900 text-xs">{k.betrieb?.name ?? k.betrieb_id}</div>
                        {k.marke && (
                          <div className="text-[10px] text-gray-400">{k.marke.marken_name}</div>
                        )}
                      </td>
                      <td className="py-3 px-4 font-mono font-semibold text-xs text-gray-800">{k.praefix}</td>
                      <td className="py-3 px-4 text-xs text-gray-600">{k.jahr}</td>
                      <td className="py-3 px-4 text-xs text-gray-600">{k.naechste_nummer}</td>
                      <td className="py-3 px-4 text-xs text-gray-600">{k.stellen}</td>
                      <td className="py-3 px-4 font-mono text-[11px] text-gray-500">
                        {formatVorschau(k.praefix, k.jahr, k.naechste_nummer, k.stellen)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setEditingKreis(k)}
                            aria-label="Bearbeiten"
                            title="Bearbeiten"
                            className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
                            disabled={!k.aktiv}
                          >
                            <Edit2 className="w-3.5 h-3.5 text-gray-400" />
                          </button>
                          <button
                            onClick={() => handleToggleAktiv(k)}
                            aria-label={k.aktiv ? 'Deaktivieren' : 'Aktivieren'}
                            title={k.aktiv ? 'Deaktivieren (Soft-Disable)' : 'Wieder aktivieren'}
                            className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
                          >
                            {k.aktiv
                              ? <Eye className="w-3.5 h-3.5 text-gray-400" />
                              : <EyeOff className="w-3.5 h-3.5 text-gray-300" />
                            }
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Neuer-Kreis-Dialog */}
          {showNeuerKreis && (
            <NeuerKreisDialog
              betriebe={betriebe ?? []}
              marken={markenFuerDialog}
              onSave={handleCreate}
              onClose={() => setShowNeuerKreis(false)}
            />
          )}

          {/* Bearbeiten-Dialog */}
          {editingKreis && (
            <BearbeitenDialog
              kreis={editingKreis}
              onSave={handleUpdate}
              onClose={() => setEditingKreis(null)}
            />
          )}
        </>
      )}
    </div>
  );
}
